// HintController — runs the LogicalSolver against the current
// `(puzzle givens + user entries)` state, returns the easiest applicable
// SolveStep mapped to a Hint, and increments `state.hintsUsed` on success.
//
// Honesty rules:
// - If the user has typed any digit that disagrees with the puzzle's solution,
//   we refuse to hint. Feeding wrong digits into the solver would surface
//   deductions built on a false premise. The scene should surface a "fix the
//   mistake first" message in that case.
// - If the board is complete or the solver gets no further within v1
//   techniques, return null.

import { recordHint, type GameStateData } from '../game/GameState.js';
import {
  nextStep,
  type SolveStep,
  type Technique,
} from '../game/LogicalSolver.js';
import {
  CELL_COUNT,
  type Digit,
  type Grid,
  type NonEmptyDigit,
} from '../game/types.js';
import type { Hint, HintElimination } from './types.js';

const TECHNIQUE_LABEL_KEY_PREFIX = 'hint.technique.';

function techniqueLabelKey(technique: Technique): string {
  return `${TECHNIQUE_LABEL_KEY_PREFIX}${technique}`;
}

function buildSolverGrid(state: GameStateData): Grid {
  const grid: Digit[] = new Array(CELL_COUNT);
  const { givens } = state.puzzle;
  const { user } = state;
  for (let i = 0; i < CELL_COUNT; i++) {
    const u = user[i] ?? 0;
    const g = givens[i] ?? 0;
    grid[i] = u !== 0 ? u : g;
  }
  return grid;
}

function isBoardComplete(grid: Grid): boolean {
  for (let i = 0; i < CELL_COUNT; i++) {
    if ((grid[i] ?? 0) === 0) return false;
  }
  return true;
}

/** True iff any user-entered digit disagrees with the puzzle's solution. */
function hasUserMistake(state: GameStateData): boolean {
  const { givens, solution } = state.puzzle;
  const { user } = state;
  for (let i = 0; i < CELL_COUNT; i++) {
    if ((givens[i] ?? 0) !== 0) continue;
    const u = user[i] ?? 0;
    if (u === 0) continue;
    if (u !== solution[i]) return true;
  }
  return false;
}

function highlightsForStep(step: SolveStep): readonly number[] {
  switch (step.technique) {
    case 'naked-single':
    case 'hidden-single':
      return [step.cellIndex];
    case 'naked-pair':
      return [step.cells[0], step.cells[1]];
    case 'pointing-pair':
    case 'box-line':
      return step.anchorCells.slice();
    case 'x-wing':
      return step.cornerCells.slice();
  }
}

function placedDigitForStep(step: SolveStep): NonEmptyDigit | null {
  if (step.technique === 'naked-single' || step.technique === 'hidden-single') {
    return step.digit;
  }
  return null;
}

function eliminationsForStep(step: SolveStep): readonly HintElimination[] {
  if (step.technique === 'naked-single' || step.technique === 'hidden-single') {
    return [];
  }
  return step.eliminations.map((e) => ({ cellIndex: e.cellIndex, digit: e.digit }));
}

function toHint(step: SolveStep): Hint {
  return {
    technique: step.technique,
    techniqueLabelKey: techniqueLabelKey(step.technique),
    highlightedCells: highlightsForStep(step),
    placedDigit: placedDigitForStep(step),
    eliminations: eliminationsForStep(step),
  };
}

export class HintController {
  /** Returns a Hint for the easiest applicable step, or `null` when the board
   * is complete / inconsistent / the user has a wrong digit / no v1 technique
   * fires. Increments `state.hintsUsed` only when a non-null Hint is returned. */
  requestHint(state: GameStateData): Hint | null {
    const grid = buildSolverGrid(state);
    if (isBoardComplete(grid)) return null;
    if (hasUserMistake(state)) return null;

    const step = nextStep(grid);
    if (step === null) return null;

    recordHint(state);
    return toHint(step);
  }
}
